/**
 * `.scroll-row`（custom.css の横スクロール行）をマウスのドラッグで送る（#548）。
 *
 * PC ではトラックパッドの横スワイプか Shift+ホイールでしか送れず、マウスだけだと
 * 最後のカードまで届かなかった。タッチはネイティブスクロールのままにするため
 * `pointerType === 'mouse'` のときだけ動かす。
 *
 * - `document` に1つだけ登録し、押した要素から `.scroll-row` を探す。
 *   RECOMMENDED ITEMS（product-recommendations）や RECENTLY VIEWED は
 *   `innerHTML` で中身が丸ごと差し替わるので、行ごとに登録すると消えてしまう
 * - ドラッグ中は `.is-dragging` を付ける（custom.css 側でスナップを止め、選択を防ぐ）
 * - 一定距離動いたら「ドラッグ」とみなし、離したときの `click` を1回だけ止める
 *   （カードのリンクに遷移しないように）
 *
 * 4つのセクションから読み込まれるが、module は URL 単位で1回しか評価されない。
 */

/** これ以上動いたらクリックではなくドラッグとみなす（px） */
const DRAG_THRESHOLD = 5;

/**
 * @type {{ row: HTMLElement, pointerId: number, startX: number, startScrollLeft: number, dragging: boolean } | null}
 */
let state = null;

/** 直前のドラッグ終了直後の `click` を止めるためのフラグ */
let suppressClick = false;

/**
 * @param {PointerEvent} event
 */
function onPointerDown(event) {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;
  if (!(event.target instanceof Element)) return;

  const row = event.target.closest('.scroll-row');
  if (!(row instanceof HTMLElement)) return;

  // はみ出していない行（1400px の RECOMMEND ITEMS など）はクリックの邪魔をしない
  if (row.scrollWidth <= row.clientWidth) return;

  state = {
    row,
    pointerId: event.pointerId,
    startX: event.clientX,
    startScrollLeft: row.scrollLeft,
    dragging: false,
  };
}

/**
 * @param {PointerEvent} event
 */
function onPointerMove(event) {
  if (!state || event.pointerId !== state.pointerId) return;

  const deltaX = event.clientX - state.startX;

  if (!state.dragging) {
    if (Math.abs(deltaX) < DRAG_THRESHOLD) return;
    state.dragging = true;
    state.row.classList.add('is-dragging');
  }

  event.preventDefault();
  state.row.scrollLeft = state.startScrollLeft - deltaX;
}

/**
 * @param {PointerEvent} event
 */
function onPointerEnd(event) {
  if (!state || event.pointerId !== state.pointerId) return;

  if (state.dragging) {
    state.row.classList.remove('is-dragging');

    // click は pointerup の直後に同期的に来る。来なかった場合（行の外で離したなど）に
    // 次の本物のクリックまで止めないよう、次のタスクで解除する
    if (event.type === 'pointerup') {
      suppressClick = true;
      setTimeout(() => {
        suppressClick = false;
      });
    }
  }

  state = null;
}

/**
 * @param {MouseEvent} event
 */
function onClick(event) {
  if (!suppressClick) return;
  suppressClick = false;
  event.preventDefault();
  event.stopPropagation();
}

/**
 * 行の中の画像・リンクはブラウザ標準のドラッグ＆ドロップが始まると
 * `pointercancel` になってしまうので、行の中で押しているあいだは止める
 * @param {DragEvent} event
 */
function onDragStart(event) {
  if (!state) return;
  event.preventDefault();
}

document.addEventListener('pointerdown', onPointerDown);
window.addEventListener('pointermove', onPointerMove);
window.addEventListener('pointerup', onPointerEnd);
window.addEventListener('pointercancel', onPointerEnd);
document.addEventListener('click', onClick, { capture: true });
document.addEventListener('dragstart', onDragStart);
